// 正規化応答(related バケット)を従来のネスト形へ復元するための宣言的な結合エンジン。
// 結合列の単一情報源は生成メタデータ RELATION_EDGES(schema.json 由来)で、各ハイドレータは
// 「どのエッジをどのバケットと結合するか」をエッジキーで宣言するだけになる。
// エッジキーと外部キー列はリテラル型なので、タイポや存在しない列の指定はコンパイルエラーになる。
// id 索引・外部キー索引の実体は relatedLookup(mapById/groupBy)を使う。
import { RELATION_EDGES, RelationEdgeKey } from "../../schema/generated/relationEdges";
import { groupBy, mapById } from "./relatedLookup";
import { required } from "../typed";

// エッジキーから外部キー列名をリテラル型のまま取り出す
type EdgeForeignKey<TKey extends RelationEdgeKey> = (typeof RELATION_EDGES)[TKey]["foreignKey"];

// direction で使えるエッジキーを静的に絞る(hasMany エッジを belongsTo 結合へ渡す誤用を防ぐ)
type EdgeKeyOf<TDirection> = { [TKey in RelationEdgeKey]: (typeof RELATION_EDGES)[TKey]["direction"] extends TDirection ? TKey : never }[RelationEdgeKey];
export type BelongsToEdgeKey = EdgeKeyOf<"belongsTo">;
export type HasManyEdgeKey = EdgeKeyOf<"hasMany">;

function foreignKeyOf<TKey extends RelationEdgeKey>(edgeKey: TKey): EdgeForeignKey<TKey> {
  return RELATION_EDGES[edgeKey].foreignKey;
}

// belongsTo 結合: 参照先バケットを id で索引し、親行 → 参照先(無ければ null)の解決関数を返す。
// 外部キーが null(未紐付け)の親行と、alive 限定バケットに参照先が入っていない(削除済み等)の
// どちらも null になる(従来 include の required:false + where deleted_at:null を踏襲)。
export function belongsToOf<TKey extends BelongsToEdgeKey, TTo extends { id: string | number }>(
  edgeKey: TKey,
  rows: TTo[]
): (parent: Record<EdgeForeignKey<TKey>, TTo["id"] | null>) => TTo | null {
  const foreignKey = foreignKeyOf(edgeKey);
  const byId = mapById(rows);
  return (parent) => {
    const foreignId = parent[foreignKey];
    if (foreignId != null) {
      return byId.get(foreignId) ?? null;
    }
    return null;
  };
}

// hasMany 結合: 子バケットを外部キー列で索引し、親 id → 子行配列(無ければ空配列)の解決関数を返す。
// 並びはバケット順(サーバーの id 昇順)を groupBy がそのまま保つ。
export function hasManyOf<TKey extends HasManyEdgeKey, TChild extends Record<EdgeForeignKey<TKey>, unknown>>(
  edgeKey: TKey,
  children: TChild[]
): (parentId: TChild[EdgeForeignKey<TKey>]) => TChild[] {
  const foreignKey = foreignKeyOf(edgeKey);
  const byForeignKey = groupBy(children, (child) => child[foreignKey]);
  return (parentId) => byForeignKey.get(parentId) ?? [];
}

// User(Group 込み)への必須 belongsTo 結合。usersWithGroupOf の索引から親行の外部キー列で解決し、
// 無ければ「<件名>(<親id>)の<役割>ユーザー(<ユーザーid>) が存在しません(データ不整合)。」で失敗する
// (従来ハイドレータのエラー文言と同形)。
export function requiredUserOf<TKey extends BelongsToEdgeKey, TUser>(
  edgeKey: TKey,
  usersById: Map<string, TUser>,
  subject: string,
  role: string
): (parent: { id: string | number } & Record<EdgeForeignKey<TKey>, string>) => TUser {
  const foreignKey = foreignKeyOf(edgeKey);
  return (parent) => required(usersById.get(parent[foreignKey]), `${subject}(${parent.id})の${role}ユーザー(${parent[foreignKey]})`);
}

// User(Group 込み)への任意 belongsTo 結合。外部キーが null、または索引に無い場合は null を保つ
// (従来 include の required:false を踏襲)。
export function userOrNullOf<TKey extends BelongsToEdgeKey, TUser>(
  edgeKey: TKey,
  usersById: Map<string, TUser>
): (parent: Record<EdgeForeignKey<TKey>, string | null>) => TUser | null {
  const foreignKey = foreignKeyOf(edgeKey);
  return (parent) => {
    const userId = parent[foreignKey];
    if (userId != null) {
      return usersById.get(userId) ?? null;
    }
    return null;
  };
}

// 参照先が null(削除済み・未紐付け)のときは null を保ち、あるときだけ内側のネストを組み立てる
export function mapNullable<TRow, TNested>(row: TRow | null, build: (row: TRow) => TNested): TNested | null {
  if (row != null) {
    return build(row);
  }
  return null;
}
